/**
 * Schemas de validação Zod
 * Define as regras de validação para autenticação e formulários
 */

import { z } from 'zod';
import { UserRole } from '@/types/auth';

/**
 * Schema para validação de login
 */
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
  password: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

const BRAZIL_UF = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const;

/**
 * Schema para validação de registro
 */
export const registerSchema = z
  .object({
    email: z
      .string()
      .min(1, 'Email é obrigatório')
      .email('Email inválido'),
    name: z
      .string()
      .min(1, 'Nome é obrigatório')
      .min(3, 'Nome deve ter pelo menos 3 caracteres')
      .max(100, 'Nome não pode ter mais de 100 caracteres'),
    password: z
      .string()
      .min(1, 'Senha é obrigatória')
      .min(8, 'Senha deve ter pelo menos 8 caracteres')
      .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
      .regex(/[0-9]/, 'Senha deve conter pelo menos um número'),
    confirmPassword: z
      .string()
      .min(1, 'Confirmação de senha é obrigatória'),
    role: z.nativeEnum(UserRole).optional().default(UserRole.PATIENT),
    crm: z.string().optional(),
    crmState: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Senhas não correspondem',
    path: ['confirmPassword'],
  })
  .superRefine((data, ctx) => {
    if (data.role !== UserRole.DOCTOR) return;

    const crm = data.crm?.trim() || '';
    const crmState = data.crmState?.trim().toUpperCase() || '';

    if (!crm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CRM é obrigatório para médicos',
        path: ['crm'],
      });
    } else if (!/^\d{4,20}$/.test(crm)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CRM deve conter apenas números (4 a 20 dígitos)',
        path: ['crm'],
      });
    }

    if (!crmState) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'UF do CRM é obrigatória',
        path: ['crmState'],
      });
    } else if (!(BRAZIL_UF as readonly string[]).includes(crmState)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'UF do CRM inválida',
        path: ['crmState'],
      });
    }
  });

export type RegisterFormData = z.infer<typeof registerSchema>;

export const BRAZIL_UF_OPTIONS = BRAZIL_UF;

/** Schema de troca de senha (mesma força do cadastro). */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Senha atual é obrigatória'),
    newPassword: z
      .string()
      .min(1, 'Nova senha é obrigatória')
      .min(8, 'Senha deve ter pelo menos 8 caracteres')
      .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
      .regex(/[0-9]/, 'Senha deve conter pelo menos um número'),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Senhas não correspondem',
    path: ['confirmPassword'],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'A nova senha deve ser diferente da senha atual',
    path: ['newPassword'],
  });

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

/** Schema de troca de e-mail (confirmação por senha no modal). */
export const changeEmailSchema = z.object({
  newEmail: z.string().min(1, 'E-mail é obrigatório').email('E-mail inválido'),
  currentPassword: z.string().min(1, 'Senha atual é obrigatória'),
});

export type ChangeEmailFormData = z.infer<typeof changeEmailSchema>;
